import React, { useState, useEffect } from 'react';
import { Label } from '../../../../../components/Form/Label/Label';
import { Grid } from '../../../../../components/Grid/Grid';
import { Product, getProductVariantPrice, getProductTaxDetails } from '../../../../../model/product';
import { t } from '../../../../../locales/translate';
import { sortBy, orderBy } from 'lodash';
import { PercentageField } from '../../../../../components/Form/TextField/PercentageField';
import { getDocumentFieldOptions } from '../../../../../model/document';
import { Order } from '../../../../../model/order';
import { handleError, handleSuccess } from '../../../../../model/notification';
import { Address } from '../../../../../model/company';
import { addItemToOrder, AddProductItem } from '../../../../../model/item';
import { deleteDelivery } from '../../../../../model/delivery';
import { deleteInvoice } from '../../../../../model/invoice';
import { AddItemsFormFooter, createDocuments, checkDocumentOption } from './AddItemsForm';
import { ScrollWrapper } from '../../../../../components/ScrollWrapper/ScrollWrapper';
import { applyTax, removeTax, formatPercentage, presence } from '../../../../../util/util';
import { cacheRequest } from '../../../../../model/cache';
import { Stack } from '../../../../../components/Stack/Stack';
import { Spacer } from '../../../../../components/Spacer/Spacer';
import { useRemoteData } from '../../../../../model/data';
import { getAllShops } from '../../../../../model/shop';
import { Icon } from '../../../../../components/Icon/Icon';
import { Line } from '../../../../../components/Line/Line';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormForm } from '../../../../../components/Form/FormForm';
import { FormSearch } from '../../../../../components/Form/AsyncSearch/FormSearch';
import { FormSelect } from '../../../../../components/Form/Select/FormSelect';
import { FormNumberField } from '../../../../../components/Form/NumberField/FormNumberField';

function canSubmit(model: AddProductItem): boolean {
  // Check required fields
  const required = !!model.productId && !!model.variantId && presence(model.price) !== undefined;
  // Check if quantity is more than 0
  const amount = model.count && model.count > 0;
  // Check if both documents are selected
  const documentSelected = !!(model.deliveryId !== 'no' || model.invoiceId !== 'no');

  return required && !!amount && documentSelected;
}

interface Props {
  order: Order;
  preferedAddresses: {
    delivery: Address;
    invoice: Address;
  };
  onClose: () => void;
  onChange: () => void;
}

export function ProductItemsForm({ order, preferedAddresses, onClose, onChange }: Props): JSX.Element {
  const [createAnother, setCreateAnother] = useState(false);
  const { model, fields, reset, updateFields } = useForm<AddProductItem>({
    model: {
      shopId: order.shopId,
      productVariants: [],
    },
    validations: {
      variantId: 'required',
      deliveryId: 'required',
      invoiceId: 'required',
    },
  });
  const [shops, shopsLoading] = useRemoteData(
    () => getAllShops(),
    (db) => db.table('shops').all
  );
  const [displayOptions, setDisplayOptions] = useState(false);

  const [loading, setLoading] = useState(false);

  // Delivery Options
  const deliveryOptions = getDocumentFieldOptions(order.deliveries, 'delivery');
  const invoiceOptions = getDocumentFieldOptions(order.invoices, 'invoice');

  function setProduct(product?: Product): void {
    if (!product) {
      const { deliveryId, invoiceId } = model;
      reset();

      return updateFields({
        deliveryId,
        invoiceId,
      });
    }

    const variant = product?.productVariants?.length === 1 ? product?.productVariants?.[0] : undefined;

    updateFields({
      productId: product?.id,
      variantId: variant?.id,
      count: 1,
      productVariants: product?.productVariants,
      price: variant && getProductVariantPrice(variant),
      deliveryId: model.deliveryId ?? deliveryOptions[0].value,
      invoiceId: model.invoiceId ?? invoiceOptions[0].value,
    });
    setTimeout(() => {
      fields.productId.onBlur();
    }, 0);
  }

  function handleOnVariantChange(value?: string | null): void {
    if (!value) {
      return fields.variantId.onChange(null);
    }

    const productVariant = model.productVariants?.find((variant) => {
      return variant.id === value;
    });

    // Prefill price for additional products
    const variantPrice = productVariant && getProductVariantPrice(productVariant);

    updateFields({
      variantId: value,
      ...(variantPrice ? { price: variantPrice } : {}),
    });
  }

  async function handleSubmit(): Promise<void> {
    setLoading(true);

    const [createdDelivery, createdInvoice] = await createDocuments({
      orderId: order.id,
      delivery: model.deliveryId === 'new',
      invoice: model.invoiceId === 'new',
      preferedAddresses,
    });

    const deliveryId = createdDelivery?.id || checkDocumentOption(model.deliveryId);
    const invoiceId = createdInvoice?.id || checkDocumentOption(model.invoiceId);

    try {
      await addItemToOrder(order.id, {
        productId: model.productId,
        price: model.price,
        count: Number(model.count),
        productVariantId: model.variantId || '',
        deliveryId,
        invoiceId,
        vatPercentage: model.vatPercentage,
        shopId: model.shopId,
      });
      if (!createAnother) {
        onClose();
      } else {
        handleSuccess(t.success.addedItems(t.model.item._name));
        updateFields({
          deliveryId: deliveryId ?? model.deliveryId ?? deliveryOptions[0].value,
          invoiceId: invoiceId ?? model.invoiceId ?? invoiceOptions[0].value,
        });
      }
      onChange();
    } catch (e) {
      handleError(e);
      // If add item fails we need to clear the documents:
      if (createdDelivery) {
        deleteDelivery(order.id, createdDelivery.id);
      }
      if (createdInvoice) {
        deleteInvoice(order.id, createdInvoice.id);
      }
    }

    setLoading(false);
  }

  const productId = model.productId;
  const productVariantId = model.variantId;
  const voucherCode = order.voucherCode?.code;

  async function fetchProductDetails(productId: string, productVariantId: string): Promise<void> {
    const vatPercentage = await cacheRequest(
      `product_${productId}_${productVariantId}_de_tax`,
      () =>
        getProductTaxDetails({
          productId,
          countryCode: 'de',
          productVariantId,
        }),
      1000 * 60 * 10 // 10 mins, we want to de-dup not cache.
    );
    fields.vatPercentage.onChange(vatPercentage);
  }

  useEffect(() => {
    if (productId && productVariantId) {
      fetchProductDetails(productId, productVariantId);
    }
  }, [productId, productVariantId]);

  return (
    <>
      <ScrollWrapper className="add-items-modal__content">
        <FormForm>
          <Stack>
            {/* Select Product */}
            <FormSearch
              inputId="productId"
              label={`${t.model.product._name}`}
              searchIn={['product', 'additional-product']}
              value={fields.productId.value}
              onChangeObject={(item) => {
                setProduct(item as Product);
              }}
              maxResults={20}
              required
            />
            {!!fields.productId.value && (
              <>
                <FormSelect
                  {...fields.variantId}
                  onChange={handleOnVariantChange}
                  label={`${t.model.category._name} / ${t.model.productVariant._name}`}
                  options={
                    model.productVariants && [
                      ...sortBy(model.productVariants, ['order']).map((variant) => ({
                        value: variant.id,
                        label: variant.description,
                      })),
                    ]
                  }
                  allowEmpty
                />
                {!!model.variantId && (
                  <>
                    <Line align="top">
                      <FormNumberField
                        label={t.model.item.quantity}
                        {...fields.count}
                        autoFocus
                        step={1}
                        style={{ flex: '1' }}
                      />
                      {model.invoiceId !== 'no' && (
                        <>
                          <FormNumberField
                            label={t.model.price.grossPriceWithVAT(formatPercentage(model.vatPercentage) ?? '')}
                            required
                            style={{ flex: '2' }}
                            value={
                              model.price && model.vatPercentage
                                ? applyTax(model.price, model.vatPercentage)
                                : model.price
                            }
                            step={0.000001}
                            onChange={(val) => {
                              fields.price.onChange(
                                Number(removeTax(Number(val), model.vatPercentage || 0).toFixed(6))
                              );
                            }}
                            actions={
                              <Icon
                                name="settings--sm"
                                onClick={() => setDisplayOptions(!displayOptions)}
                                tooltip={t.model.price.editDetails}
                              />
                            }
                            description={
                              voucherCode ? t.model.order.voucherDetails.voucherCodeApplied(voucherCode) : undefined
                            }
                          />
                        </>
                      )}
                    </Line>
                    {model.invoiceId !== 'no' && displayOptions && (
                      <>
                        <Grid noStretch>
                          <PercentageField
                            label={`${t.model.price.vat} %`}
                            value={fields.vatPercentage.value}
                            onChange={(value) => fields.vatPercentage.onChange(value)}
                            required={fields.vatPercentage.required}
                          />
                          <FormNumberField label={t.model.price.netPrice} {...fields.price} step={6} required />
                        </Grid>
                        <FormSelect
                          {...fields.shopId}
                          label={t.model.shop._name}
                          options={orderBy(shops, 'name').map((e) => ({ value: e.id, label: e.name }))}
                          loading={shopsLoading}
                          allowEmpty
                        />
                      </>
                    )}
                  </>
                )}
                <Spacer size="none" />
                <Label title={`${t.model.invoice._name} / ${t.model.delivery._name}`} />
                <Grid>
                  <FormSelect
                    {...fields.deliveryId}
                    style={{ marginBottom: 'var(--spacing-md)' }}
                    label={t.model.delivery._name}
                    options={deliveryOptions}
                    inputId="deliveryId"
                  />
                  <FormSelect
                    {...fields.invoiceId}
                    style={{ marginBottom: 'var(--spacing-md)' }}
                    label={t.model.invoice._name}
                    options={invoiceOptions}
                    inputId="invoiceId"
                  />
                </Grid>
              </>
            )}
          </Stack>
        </FormForm>
      </ScrollWrapper>
      <AddItemsFormFooter
        loading={loading}
        onClose={onClose}
        createAnother={createAnother}
        onChangeCreateAnother={() => setCreateAnother(!createAnother)}
        handleSubmit={handleSubmit}
        canSubmit={canSubmit(model)}
      ></AddItemsFormFooter>
    </>
  );
}
